import { useEffect, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { GridLoader } from 'react-spinners';
import localStorage from 'redux-persist/es/storage';
import { environment } from '../../../../environments/environment';
import { useAppSelector } from '../../../common/store/hooks';
import { selectPersonalState } from '../../../common/store/personal.slice';
import { selectTenantConfig } from '../../../common/store/tenant-config/tenant-config.slice';
import { useDeviceBounds } from '../../../common/hooks/useDeviceBounds';
import { BetCtaComponentLabels } from '../../../common/labels/bet-cta-component.labels';
import './bet-cta-component.scss';
import { selectSavingsState } from '../../../common/store/savings.slice';
import { selectAssetsState } from '../../../common/store/assets/assets.slice';

type BetCtaFormInputs = {
  firstName: string;
  lastName: string;
  email: string;
  telephoneNumber: string;
  postalCode: string;
};

type ReportLoadingReadyMessage = {
  type: 'report-loading-ready';
};

type ReportLoadingTextMessage = {
  type: 'report-loading-message';
  text: string;
};

type BetCtaParentMessage = {
  type: 'retirement_planner_save_open' | 'retirement_planner_lead_submitted';
};

// Notify the embedding host page (when this app runs inside an iframe) about
// BetCtaComponent lifecycle events. This lives inside BetCtaComponent (not the
// reusable CtaModalComponent) so the messages are only ever posted when the
// Bethmann CTA is used.
function postMessageToParent(type: BetCtaParentMessage['type']) {
  // window.parent === window when not embedded in an iframe.
  if (window.parent === window) {
    return;
  }

  // The host origin is not known to this app, so target any origin. No
  // sensitive data is included in the message payload.
  window.parent.postMessage({ event: type }, '*');
}

export interface BetCtaComponentProps {
  betCtaLabels: BetCtaComponentLabels;
}

async function generatePdfReport(
  tenantName: string,
  reportTitle: string,
  assets: ReturnType<typeof selectAssetsState>,
  savings: ReturnType<typeof selectSavingsState>
): Promise<{ blob: Blob; base64: string } | null> {
  try {
    if (
      assets.cash === 0 &&
      assets.bonds === 0 &&
      assets.equity === 0 &&
      assets.realEstate === 0 &&
      savings.lumpsum === 0 &&
      savings.monthly === 0
    ) {
      return null;
    }

    const stateString = await localStorage.getItem('persist:root');
    if (!stateString) return null;

    const stateBase64String = btoa(stateString!);
    const exportRequest = {
      name: tenantName,
      path: 'report',
      selector: 'report-loaded',
      title: reportTitle,
      state: stateBase64String,
    };

    const response = await fetch(`${environment.apiBaseUrl}/export`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(exportRequest),
    });

    if (!response.ok) return null;

    const blob = await response.blob();

    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        // Strip the "data:application/pdf;base64," prefix
        const base64Data = dataUrl.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

    return { blob, base64 };
  } catch (error) {
    console.error('Error generating PDF report:', error);
    return null;
  }
}

export function BetCtaComponent({ betCtaLabels }: BetCtaComponentProps) {
  const personal = useAppSelector(selectPersonalState);
  const tenantConfig = useAppSelector(selectTenantConfig);
  const assets = useAppSelector(selectAssetsState);
  const savings = useAppSelector(selectSavingsState);
  const { isHorizontallyCompact } = useDeviceBounds();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const reportPopupRef = useRef<Window | null>(null);
  const hasPostedOpenRef = useRef(false);

  const loaderColor = getComputedStyle(document.documentElement).getPropertyValue('--secondary-0');

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<BetCtaFormInputs>({
    reValidateMode: 'onBlur',
    mode: 'onBlur',
  });

  const watchedFields = watch();
  const allFieldsFilled =
    !!watchedFields.firstName?.trim() &&
    !!watchedFields.lastName?.trim() &&
    !!watchedFields.email?.trim() &&
    // Telephone number is optional, so we don't require it to be filled for the submit button to be enabled. However, if it's filled, it must not be just whitespace.
    //!!watchedFields.telephoneNumber?.trim() &&
    !!watchedFields.postalCode?.trim();

  const onSubmit: SubmitHandler<BetCtaFormInputs> = async (data) => {
    const params = new URLSearchParams(window.location.search);
    setIsLoading(true);
    setSubmitError(null);

    let pdfBase64: string | null = null;
    const reportPopup = reportPopupRef.current;
    const tenantName = tenantConfig.name ?? '';
    const pdfResult = await generatePdfReport(tenantName, betCtaLabels.REPORT_TITLE, assets, savings);

    if (pdfResult) {
      pdfBase64 = pdfResult.base64;

      if (reportPopup && !reportPopup.closed) {
        // If the popup is still open, update its content with the generated PDF
        const pdfUrl = URL.createObjectURL(pdfResult.blob);
        reportPopup.location.href = pdfUrl;
        // Clean up the object URL after some time to free memory
        setTimeout(() => URL.revokeObjectURL(pdfUrl), 30000);
      }
    }

    try {
      const stateString = await localStorage.getItem('persist:root');

      const body = JSON.stringify({
        layoutId: tenantConfig.layoutId,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        telephoneNumber: data.telephoneNumber,
        postalCode: data.postalCode,
        age: personal.age,
        utmSource: params.get('utm_source'),
        utmMedium: params.get('utm_medium'),
        utmCampaign: params.get('utm_campaign'),
        utmTerm: params.get('utm_term'),
        utmContent: params.get('utm_content'),
        financialsJson: stateString,
        pdfReport: pdfBase64,
      });

      const response = await fetch(`${environment.apiBaseUrl}/contactform`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body,
      });

      if (response.ok) {
        setIsSubmitted(true);
        postMessageToParent('retirement_planner_lead_submitted');
      } else {
        console.error('Error submitting form:', response.statusText);
        setSubmitError(betCtaLabels.ERROR);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError(betCtaLabels.ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsSubmitDisabled(!consentChecked || !allFieldsFilled || isLoading);
  }, [consentChecked, allFieldsFilled, isLoading]);

  // BetCtaComponent only mounts when the CTA is opened (it is the conditional
  // child of the modal / edit container), so mount is the "opened" event.
  // The ref guards against React StrictMode invoking this effect twice in dev;
  // it persists across StrictMode's simulated remount but resets on a real one.
  useEffect(() => {
    if (hasPostedOpenRef.current) {
      return;
    }
    hasPostedOpenRef.current = true;
    postMessageToParent('retirement_planner_save_open');
  }, []);

  if (isSubmitted) {
    return (
      <div className="bet-cta-success" role="status">
        <p>{betCtaLabels.SUCCESS}</p>
      </div>
    );
  }

  const rowClass = isHorizontallyCompact ? 'bet-cta-row--single' : 'bet-cta-row--double';

  const openEmptyTabAndSubmit = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    const tenantName = tenantConfig.name ?? '';
    const reportLoadingUrl = tenantName
      ? `${window.location.origin}/${tenantName}/report-loading`
      : `${window.location.origin}/report-loading`;

    // Prevent default to control the navigation flow manually
    event.preventDefault();

    // Use !consentChecked || !allFieldsFilled || isLoading to disable the anchor and prevent this function from being called when the form is not ready for submission
    if (!consentChecked || !allFieldsFilled || isLoading) {
      return;
    }

    // Nothing specified, then no report to generate.
    if (
      assets.cash === 0 &&
      assets.bonds === 0 &&
      assets.equity === 0 &&
      assets.realEstate === 0 &&
      savings.lumpsum === 0 &&
      savings.monthly === 0
    ) {
      return;
    }

    // Open a new tab immediately to avoid popup being blocked. The content will be updated later once the PDF is generated.
    reportPopupRef.current = window.open(reportLoadingUrl, '_blank');

    if (reportPopupRef.current) {
      const popupWindow = reportPopupRef.current;
      const loadingMessage = betCtaLabels.REPORT_LOADING;

      const postLoadingMessage = () => {
        const message: ReportLoadingTextMessage = {
          type: 'report-loading-message',
          text: loadingMessage,
        };

        popupWindow.postMessage(message, window.location.origin);
      };

      let intervalId = 0;

      const onPopupReady = (messageEvent: MessageEvent<ReportLoadingReadyMessage>) => {
        if (messageEvent.origin !== window.location.origin) {
          return;
        }

        if (messageEvent.source !== popupWindow) {
          return;
        }

        if (messageEvent.data?.type !== 'report-loading-ready') {
          return;
        }

        postLoadingMessage();
        window.clearInterval(intervalId);
        window.removeEventListener('message', onPopupReady);
      };

      window.addEventListener('message', onPopupReady);

      // Retry a few times in case the ready signal is delayed.
      let attempts = 0;
      const maxAttempts = 20; // total 5 seconds
      intervalId = window.setInterval(() => {
        if (popupWindow.closed || attempts >= maxAttempts) {
          window.clearInterval(intervalId);
          window.removeEventListener('message', onPopupReady);
          return;
        }

        postLoadingMessage();
        attempts += 1;
      }, 250);
    }

    // Call form submit
    handleSubmit(onSubmit)();
  };

  return (
    <form className="bet-cta-component" onSubmit={handleSubmit(onSubmit)} noValidate>
      {isLoading ? (
        <div className="bet-cta-loading-space" aria-live="polite" aria-busy="true">
          <GridLoader className="spinner" loading={isLoading} color={loaderColor} />
        </div>
      ) : (
        <>
          <div className="bet-cta-section">
            <h4 className="bet-cta-section-title">{betCtaLabels.SECTION_DETAILS}</h4>
            <div className={`bet-cta-row ${rowClass}`}>
              <div className="bet-cta-field">
                <label className="bet-cta-label" htmlFor="bet-cta-first-name">
                  {betCtaLabels.FIELDS.FIRST_NAME}
                </label>
                <input
                  id="bet-cta-first-name"
                  placeholder={betCtaLabels.FIELDS.FIRST_NAME_PLACEHOLDER}
                  className={errors.firstName ? 'error' : ''}
                  aria-invalid={!!errors.firstName || undefined}
                  aria-describedby="bet-cta-first-name-error"
                  {...register('firstName', { required: true })}
                />
                {errors.firstName && (
                  <span id="bet-cta-first-name-error" className="error">
                    {betCtaLabels.REQUIRED}
                  </span>
                )}
              </div>
              <div className="bet-cta-field">
                <label className="bet-cta-label" htmlFor="bet-cta-last-name">
                  {betCtaLabels.FIELDS.LAST_NAME}
                </label>
                <input
                  id="bet-cta-last-name"
                  placeholder={betCtaLabels.FIELDS.LAST_NAME_PLACEHOLDER}
                  className={errors.lastName ? 'error' : ''}
                  aria-invalid={!!errors.lastName || undefined}
                  aria-describedby="bet-cta-last-name-error"
                  {...register('lastName', { required: true })}
                />
                {errors.lastName && (
                  <span id="bet-cta-last-name-error" className="error">
                    {betCtaLabels.REQUIRED}
                  </span>
                )}
              </div>
            </div>
            <div className="bet-cta-row bet-cta-row--single">
              <div className="bet-cta-field">
                <label className="bet-cta-label" htmlFor="bet-cta-email">
                  {betCtaLabels.FIELDS.EMAIL}
                </label>
                <input
                  id="bet-cta-email"
                  type="email"
                  placeholder={betCtaLabels.FIELDS.EMAIL_PLACEHOLDER}
                  className={errors.email ? 'error' : ''}
                  aria-invalid={!!errors.email || undefined}
                  aria-describedby="bet-cta-email-error"
                  {...register('email', {
                    required: true,
                    // eslint-disable-next-line no-useless-escape
                    pattern:
                      /^(([^<>()\[\]\\.,;:\s@\"]+(\.[^<>()\[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                  })}
                />
                {errors.email && (
                  <span id="bet-cta-email-error" className="error">
                    {betCtaLabels.REQUIRED}
                  </span>
                )}
              </div>
            </div>
            <div className={`bet-cta-row ${rowClass}`}>
              <div className="bet-cta-field">
                <label className="bet-cta-label" htmlFor="bet-cta-telephone">
                  {betCtaLabels.FIELDS.TELEPHONE}
                </label>
                <input
                  id="bet-cta-telephone"
                  placeholder={betCtaLabels.FIELDS.TELEPHONE_PLACEHOLDER}
                  className={errors.telephoneNumber ? 'error' : ''}
                  aria-invalid={!!errors.telephoneNumber || undefined}
                  aria-describedby="bet-cta-telephone-error"
                  {...register('telephoneNumber', {
                    required: false,
                    pattern: /^\+?[\d\s\-]+$/,
                  })}
                />
                {errors.telephoneNumber && (
                  <span id="bet-cta-telephone-error" className="error">
                    {betCtaLabels.REQUIRED}
                  </span>
                )}
              </div>
              <div className="bet-cta-field">
                <label className="bet-cta-label" htmlFor="bet-cta-postal-code">
                  {betCtaLabels.FIELDS.POSTAL_CODE}
                </label>
                <input
                  id="bet-cta-postal-code"
                  placeholder={betCtaLabels.FIELDS.POSTAL_CODE_PLACEHOLDER}
                  className={errors.postalCode ? 'error' : ''}
                  aria-invalid={!!errors.postalCode || undefined}
                  aria-describedby="bet-cta-postal-code-error"
                  {...register('postalCode', {
                    required: true,
                    pattern: /^\d{4,6}$/,
                  })}
                />
                {errors.postalCode && (
                  <span id="bet-cta-postal-code-error" className="error">
                    {betCtaLabels.REQUIRED}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="bet-cta-section">
            <h4 className="bet-cta-section-title">{betCtaLabels.SECTION_CONSENT}</h4>
            <label className="bet-cta-consent" htmlFor="bet-cta-consent">
              <input
                id="bet-cta-consent"
                type="checkbox"
                aria-label={betCtaLabels.SECTION_CONSENT}
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
              />
              <span className="bet-cta-disclaimer" dangerouslySetInnerHTML={{ __html: betCtaLabels.DISCLAIMER }} />
            </label>
          </div>

          {submitError && (
            <p className="bet-cta-error" role="alert">
              {submitError}
            </p>
          )}
        </>
      )}

      <div className="bet-cta-submit">
        <a href="#" onClick={openEmptyTabAndSubmit} className={`button button--solid ${isSubmitDisabled ? 'button--disabled' : ''}`}>
          {betCtaLabels.SUBMIT}
        </a>
      </div>
    </form>
  );
}

export default BetCtaComponent;
